import type { TicketPriority, TicketStatus } from '../../types/bpTickets';

/**
 * FINN design-system label colours (finn-design-branding skill): blue = actionable,
 * green = active/normal, orange = warning, red = critical, gray (iron/pearl) = neutral.
 * Chosen by what each value actually means rather than by keeping every value visually
 * distinct — Resolved and Cancelled are both "closed, no more attention needed" and
 * deliberately share grey; their labels tell them apart.
 */
const CHIP_CLASS = {
  blue: 'bg-lightBlue text-trustedBlue',
  green: 'bg-lightGreen text-green',
  orange: 'bg-lightOrange text-orange',
  red: 'bg-lightRed text-red',
  gray: 'bg-pearl text-iron',
} as const;

const STATUS_CHIP: Record<TicketStatus, string> = {
  Submitted: CHIP_CLASS.blue, // new, needs a first look
  'In Progress': CHIP_CLASS.green, // actively being handled
  'Waiting for Employee': CHIP_CLASS.orange, // blocked on someone else
  Resolved: CHIP_CLASS.gray, // closed
  Cancelled: CHIP_CLASS.gray, // closed
};

const PRIORITY_CHIP: Record<TicketPriority, string> = {
  Regular: CHIP_CLASS.gray,
  Urgent: CHIP_CLASS.red,
};

/**
 * A chip class that is always defined.
 *
 * The maps above are keyed on the domain enums, but the values reaching them
 * come from Airtable select options — display text someone can rename in the
 * UI at any time. Indexing directly and reading the result as a class name
 * produced `undefined` and took down both ticket lists (the Airtable priority
 * options are "🤝 Regular" / "⚠️ Urgent", which never matched). A rename
 * should cost a grey chip, not the page.
 */
export function statusChipClass(status: string): string {
  return STATUS_CHIP[status as TicketStatus] ?? CHIP_CLASS.gray;
}

export function priorityChipClass(priority: string): string {
  return PRIORITY_CHIP[priority as TicketPriority] ?? CHIP_CLASS.gray;
}
